<template>
    <md-progress-spinner v-if="isLoading"
        md-mode="indeterminate"></md-progress-spinner>
    <div v-else
        class="card">
        <div class="print-preview">
            <logo-horizontal></logo-horizontal>
            <div class="print-btn-top"
                style="display:none">
                <md-button class="md-raised md-primary"
                    @click="printReport">
                    <md-icon>print</md-icon>
                    {{ $t('general.print') }}
                </md-button>
            </div>
        </div>
        <div v-if="inCart.length > 0">
            <table v-for="(order, index) in inCart"
                :key="index"
                class="table table-sm">
                <thead>
                    <tr>
                        <th scope="col"
                            style="width:10%">
                            {{ $tc('orders.order', 1) }}
                        </th>
                        <th scope="col"
                            style="width:18%">
                            {{ $t('orders.order-status') }}
                        </th>
                        <th scope="col"
                            style="width:20%">
                            {{ $tc('orders.client', 1) }}
                        </th>
                        <th scope="col"
                            style="width:18%">
                            {{ $tc('orders.delivery-address') }}
                        </th>
                        <th scope="col"
                            style="width:19%">
                            {{ $t('orders.shipping-date') }}
                        </th>
                        <th scope="col"
                            style="width:15%">
                            {{ $t('orders.delivery-date') }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>
                            <span>
                                {{ order.order_id ? order.order_id : '--' }}
                            </span>
                        </td>
                        <td>
                            <span style="text-transform:uppercase">
                                {{ order.order_status }}
                            </span>
                        </td>
                        <td>
                            <span>
                                {{ order.organization_name ? order.organization_name : '' }}
                                {{ " (" }}
                                {{ order.first_name ? order.first_name : '' }} {{ order.last_name ? order.last_name: '' }}
                                {{ ")" }}
                            </span>
                        </td>
                        <td>
                            <span>
                                {{ (order.address !== '') ? order.address : '-' }}
                            </span>
                        </td>
                        <td>
                            <span>
                                {{ order.shipped_date }}
                            </span>
                        </td>
                        <td>
                            <span>
                                {{ order.delivery_date }}
                            </span>
                        </td>
                    </tr>
                    <tr>
                        <td colspan="6"
                            style="padding:0 2.5%">
                            <table class="table table-condensed table-sm">
                                <thead>
                                    <tr>
                                        <th scope="col"
                                            style="width:12%">
                                            {{ $tc('products.product', 2) }}
                                        </th>
                                        <th scope="col"
                                            style="width:12%">
                                            {{ $tc('products.quantity', 1) }}
                                        </th>
                                        <th scope="col"
                                            style="width:12%">
                                            {{ $tc('products.caliber', 1) }}
                                        </th>
                                        <th scope="col"
                                            style="width:10%">
                                            {{ $t('products.status') }}
                                        </th>
                                        <th scope="col"
                                            style="width:10%">
                                            {{ $t('products.preparation') }}
                                        </th>
                                        <th scope="col"
                                            style="width:14%">
                                            {{ $tc('products.item-status', 2) }}
                                        </th>
                                        <th scope="col"
                                            style="width:30%">
                                            {{ $tc('products.observation', 2) }}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="product in order.products"
                                        :key="product.uuid">
                                        <td v-if="product.active">
                                            <span style="text-transform:uppercase">
                                                {{ product.product_name }}
                                            </span>
                                        </td>
                                        <td v-if="product.active">
                                            <span>
                                                {{ product.quantity }} ({{ product.units }})</span>
                                        </td>
                                        <td v-if="product.active">
                                            <span>
                                                {{ product.caliber }}
                                            </span>
                                        </td>
                                        <td v-if="product.active">
                                            <span>
                                                {{ product.product_status }}
                                            </span>
                                        </td>
                                        <td v-if="product.active">
                                            <span>
                                                {{ product.prepared === true ? $t('general.yes') : $t('general.no') }}
                                            </span>
                                        </td>
                                        <td v-if="product.active">
                                            <span>
                                                {{ getOrderItemStatus(product.order_item_status_id) }}
                                            </span>
                                        </td>
                                        <td v-if="product.active">
                                            <span>
                                                {{ product.notes }}
                                            </span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </td>
                    </tr>
                    <td v-if="order.description"
                        colspan="6"
                        style="padding:0 2.5%;text-align:left">
                        <tr>
                            <th>
                                {{ $t('orders.order-notes') }}
                            </th>
                        </tr>
                        <tr>
                            <td style="border-top:none">
                                <p>{{ order.description }}</p>
                            </td>
                        </tr>
                    </td>
                </tbody>
            </table>
        </div>
        <div class="print-preview">
            <div class="print-btn-bottom">
                <md-button class="md-raised md-primary"
                    @click="printReport">
                    <md-icon>print</md-icon>
                    {{ $t('general.print') }}
                </md-button>
            </div>
        </div>
    </div>
</template>

<script>
import _ from 'lodash'
import ordersService from '@/components/orders/services/'
import organizationsService from '@/components/organizations/services/organizations'
import LogoHorizontal from '@/components/ui/LogoHorizontal.vue'
import luxon from '@/mixins/luxon'
import { DateTime } from 'luxon'
import OrderStatus from '@/utils/orderStatus'
import { mapGetters } from 'vuex'

const loadingKeys = {
    fetchingItem: 'fetching-item'
}

export default {
    name: 'OrdersReportPreview',

    mixins: [luxon],

    components: {
        LogoHorizontal
    },

    data () {
        return {
            orders: [],
            loadings: [],
            printMode: false
        }
    },

    computed: {
        ...mapGetters([
            'historicCart',
            'printPreview'
        ]),

        isLoading () {
            return this.isFetchingProducts
        },

        isFetchingProducts () {
            return !!this.loadings.filter(
                loading => loading.key === loadingKeys.fetchingItem
            ).length
        },

        inCart () {
            return this.$store.getters.historicCart || []
        }
    },

    methods: {
        printReport () {
            window.print()
        },

        async fetchProducts (orderId) {
            let self = this

            _.fill(self.loadings, loadingKeys.fetchingItem)

            let params = {
                filter: {
                    order_id: orderId
                }
            }

            let response = await ordersService.retrieveItems(params)

            _.pull(self.loadings, loadingKeys.fetchingItem)

            return response.data
        },

        getOrderItemStatus (id) {
            return OrderStatus.getOrderItemStatus(id)
        },

        concatOrganizationAddress (organization) {
            return (
                (organization[0].street ? organization[0].street + '\n' : '') +
                (organization[0].zip_code ? organization[0].zip_code + '\n' : '') +
                (organization[0].city ? organization[0].city + '\n' : '')
            )
        },

        async fetchOrganization (organizationId) {
            let self = this

            let params = {
                filter: {
                    organization_id: organizationId
                }
            }

            let response = await organizationsService.retrieve(params)

            return self.concatOrganizationAddress(response.data.results)
        }
    },

    async created () {
        let data = this.$store.getters.printPreview

        // if has data this screen is called from the print button on orders datatable screen
        if (data && data.length) {
            // clears the historic cart
            this.$store.dispatch('emptyHistoricCart')

            this.printMode = true

            for (const order of data) {
                // modify date formats that are being sent from database
                order.delivery_date = order.delivery_date ? DateTime.fromISO(order.delivery_date).toFormat('yyyy-LL-dd HH:mm') : ''
                order.shipped_date = order.shipped_date ? DateTime.fromISO(order.shipped_date).toFormat('yyyy-LL-dd HH:mm') : ''

                order.products = await this.fetchProducts(order.order_id)
                order.address = await this.fetchOrganization(order.organization_id)

                this.$store.dispatch('loadHistoricCart', order)
            }
        }
    }
}
</script>
